import { PureComponent } from 'react';
import ReactTable from 'react-table-contextmenu';
import 'react-table-contextmenu/react-table.css';
import withFixedColumns from 'react-table-hoc-fixed-columns';
import 'react-table-hoc-fixed-columns/lib/styles.css';

import { isEqual } from 'lodash';
import PropTypes from 'prop-types';

import { Box } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

import ColumnsVisibilityComponent from './ColumnsVisibilityComponent';
import NoTableDataComponent from './NoDataComponent';

import { customSorting, getIsReadOnlyAdmin } from '../../utils';
import {
  getColumnsWidth,
  getHiddenColumns,
  getUpdatedExpandedRows,
  handleOnClickProps,
  handleStylesProps,
  saveColumnsWidth,
  saveHiddenColumns,
} from './utils';

import {
  READ_ONLY_ADMIN_HIDDEN_COLUMNS,
  TABLE_ENTRIES_LIMIT,
} from './constants';

import './styles.css';

import { captureException } from '../../sentryInit';
import './fixed-columns-styles.css';

const ReactTableFixedColumns = withFixedColumns(ReactTable);

class TableComponent extends PureComponent {
  constructor(props) {
    super(props);
    const { defaultSortKey, tableId, columns } = this.props;
    this.state = {
      sortKey: defaultSortKey || null,
      columnsWidth: getColumnsWidth(tableId, columns),
      hiddenColumns: getHiddenColumns(tableId, columns),
      expanded: {},
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const { sortKey, expanded } = this.state;
    const {
      onOrderList,
      entries,
      tableId,
      expandedByDefault = {},
    } = this.props;

    if (onOrderList && prevState.sortKey !== sortKey) {
      onOrderList({ sortKey });
    }

    let expandedRows = { ...expanded };

    if (
      Object.values(expandedByDefault).length &&
      !isEqual(expandedByDefault, prevProps?.expandedByDefault)
    ) {
      expandedRows = { ...expandedRows, ...expandedByDefault };
    }

    if (Object.values(expandedRows).length > 0) {
      const prevIds = prevProps?.entries?.map(({ id }) => id);
      const newIds = entries?.map(({ id }) => id);
      const hasNewEntries = !isEqual(prevIds, newIds);

      hasNewEntries &&
        this.setState((prevState) => ({
          ...prevState,
          expanded: getUpdatedExpandedRows(expandedRows, entries),
        }));
    }

    if (entries?.length > TABLE_ENTRIES_LIMIT) {
      // err is sent to Sentry, no need to translate
      captureException(
        `${tableId} received ${entries?.length} entries when a limit is set to ${TABLE_ENTRIES_LIMIT}`,
      );
    }
  }

  setColumnsWidth = (columnsWidth) => {
    const { tableId } = this.props;
    this.setState({ columnsWidth });
    saveColumnsWidth(tableId, columnsWidth);
  };

  hideColumn = (columnId) => {
    const { tableId } = this.props;
    const { hiddenColumns } = this.state;
    const newHiddenColumns = [...hiddenColumns, columnId];
    this.setState({ hiddenColumns: newHiddenColumns });
    saveHiddenColumns(tableId, newHiddenColumns);
  };

  showColumn = (columnId) => {
    const { tableId } = this.props;
    const { hiddenColumns } = this.state;
    const newHiddenColumns = [...hiddenColumns].filter((id) => id !== columnId);
    this.setState({ hiddenColumns: newHiddenColumns });
    saveHiddenColumns(tableId, newHiddenColumns);
  };

  onHiddenColumnsChange = (event, columnId) => {
    const isChecked = event.target.checked;
    isChecked ? this.showColumn(columnId) : this.hideColumn(columnId);
  };

  render() {
    const {
      tableId,
      columns,
      entries,
      manual,
      defaultSort,
      sortFunction,
      onRowClick,
      rowStyles,
      defaultSortDesc,
      contextMenuId,
      SubComponent,
      isMobileScreen,
    } = this.props;
    const { columnsWidth, hiddenColumns, expanded } = this.state;

    const isReadOnlyAdmin = getIsReadOnlyAdmin();

    const availableColumns = columns.filter(({ id }) =>
      isReadOnlyAdmin ? !READ_ONLY_ADMIN_HIDDEN_COLUMNS.includes(id) : true,
    );

    const visibleColumns = availableColumns
      .filter(({ id }) => !hiddenColumns.includes(id))
      .map((column) => {
        const { Header, ...rest } = column;
        if (typeof Header === 'string') {
          return {
            Header: (
              <Tooltip title={Header} placement="top" arrow>
                <Box>{Header}</Box>
              </Tooltip>
            ),
            ...rest,
          };
        }
        return column;
      });

    const tableStyle = isMobileScreen
      ? {
          maxHeight: `${window.innerHeight - 170}px`,
        }
      : {};

    return (
      <Box my={1} id="sticky-table" position="relative">
        <ColumnsVisibilityComponent
          tableId={tableId}
          columns={availableColumns}
          hiddenColumnsIds={hiddenColumns}
          onHiddenColumnsChange={this.onHiddenColumnsChange}
        />
        <ReactTableFixedColumns
          data={entries}
          columns={visibleColumns}
          resized={columnsWidth}
          defaultPageSize={TABLE_ENTRIES_LIMIT}
          minRows={1}
          className="-striped -highlight"
          style={tableStyle}
          showPagination={false}
          manual={manual}
          defaultSorted={defaultSort}
          defaultSortDesc={defaultSortDesc}
          defaultSortMethod={customSorting}
          onSortedChange={(params) => sortFunction(params)}
          onResizedChange={(newResized) => this.setColumnsWidth(newResized)}
          contextMenuId={contextMenuId}
          collect={(data) => contextMenuId && onRowClick(data, true)}
          getTrProps={(state, rowInfo) => {
            const onClickProps = isReadOnlyAdmin
              ? { onClick: () => {} }
              : handleOnClickProps(rowInfo, onRowClick);
            const rowStylesProps = handleStylesProps(
              rowInfo,
              rowStyles,
              isReadOnlyAdmin,
              tableId,
            );
            return { ...onClickProps, ...rowStylesProps };
          }}
          SubComponent={SubComponent}
          expanded={expanded}
          onExpandedChange={(expanded) => {
            this.setState((prevState) => ({
              ...prevState,
              expanded: getUpdatedExpandedRows(expanded, entries),
            }));
          }}
          NoDataComponent={NoTableDataComponent}
        />
      </Box>
    );
  }
}

TableComponent.defaultProps = {
  manual: false,
  defaultSortKey: '',
  defaultSort: [{}],
  defaultSortDesc: false,
  sortFunction: () => {},
  onRowClick: () => {},
  contextMenuId: '',
  rowStyles: null,
  onOrderList: null,
  SubComponent: null,
  isMobileScreen: false,
};

TableComponent.propTypes = {
  tableId: PropTypes.string.isRequired,
  columns: PropTypes.instanceOf(Array).isRequired,
  entries: PropTypes.instanceOf(Array).isRequired,
  manual: PropTypes.bool,
  defaultSortKey: PropTypes.string,
  defaultSort: PropTypes.instanceOf(Array),
  sortFunction: PropTypes.func,
  onRowClick: PropTypes.func,
  onOrderList: PropTypes.func,
  SubComponent: PropTypes.func,
  contextMenuId: PropTypes.string,
  rowStyles: PropTypes.instanceOf(Object),
  expandedByDefault: PropTypes.instanceOf(Object),
  defaultSortDesc: PropTypes.bool,
  isMobileScreen: PropTypes.bool,
};

export default TableComponent;
